import { FormControl, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getShelfLifeReportEnquiry, exportShelfLifeAllCustomer } from "services/data.report.service";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { ShelfLifeReportEnquiryColumns } from "./columns";
import { useState, useEffect } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { CSVLink } from "react-csv";
import { LoadingDialog, Error_ } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import { getCustomerOptions } from "services/data.internal.service";
import { SelectFields } from "ui-components/Form";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import useFileDownloadHook from "hooks/useFileDownloadHook";
import dayjs from "dayjs";

function ShelfLifeReportEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode, customerOpt } = useCustomerValue();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [type, setType] = useState("ALL");
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const { downloadFile, isDownloading: isExporting, error: exportError } = useFileDownloadHook();
  const [alert, setAlert] = useState(null);

  const [customers, setCustomers] = useState([]);
  const isAllCustomer = customers.includes("ALL CUSTOMER");

  const [customerOptions, setCustomerOptions] = useState([]);

  useEffect(() => {
    if (exportError) {
      setAlert(<Error_ content={exportError.message} onChange={setAlert} />);
    }
  }, [exportError]);

  const onExportAllCustomer = () => {
    const filename = `ALLCUSTOMER_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_ShelfLifeReport.xlsx`;
    downloadFile(() => exportShelfLifeAllCustomer({ type }), filename);
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["shelf-life-report-enquiry", { customers, type }],
    queryFn: () => getShelfLifeReportEnquiry({ customerCode, customers, type }),
    enabled: Boolean(customerCode) && Boolean(customers.length) && !isAllCustomer,
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    // onSuccess: (fetchedData) => {
    //   // Transform the data to add a single quote to the SKU code
    //   const modifiedData = fetchedData.data.data.map(item => ({
    //     ...item,
    //     //skuCode: `'${item.skuCode}`
    //     skuCode: item.skuCode.padStart(10, '0')
    //   }));
    //   setData(modifiedData);
    // },
    onError: () => {},
  });

  useQuery({
    queryKey: ["customer-options", { customerCode }],
    queryFn: () => getCustomerOptions(),
    onSuccess: (data) => {
      const activeCust = customerOpt.filter((x) => data.data.some((y) => y === x));
      setCustomerOptions(activeCust);
      setCustomers([customerCode]);
    },
    onError: () => {},
  });

  const exportPermission =
    allowAll ||
    (Boolean(permissions["Shelf Life_Report"]) &&
      permissions["Shelf Life_Report"].findIndex((b) => b === "export") !== -1);

  const headers = [
    { label: "Customer Code", key: "customerCode" },
    { label: "Warehouse Code", key: "warehouseCode" },
    { label: "Zone ID", key: "zoneID" },
    { label: "Location ID", key: "locationID" },
    { label: "Vendor Code", key: "vendorCode" },
    { label: "SKU Code", key: "skuCode" },
    { label: "MLN", key: "mln" },
    { label: "Expiry Date", key: "expiryDate" },
    { label: "Days To Expired", key: "daysToExpired" },
    { label: "SKU Shelf Life", key: "skuShelfLife" },
    { label: "SKU Descriptions", key: "skuDescriptions" },
    { label: "Quantity", key: "quantity" },
    { label: "Base UOM", key: "baseUOM" },
  ];

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  return (
    <ListLayout>
      <TableCard sx={{ my: 3 }}>
        {isFetching && <LoadingDialog />}
        {(isDownloading || isExporting) && <ExportDialog />}
        {alert && <MDBox sx={{ mx: 3, mt: 3 }}>{alert}</MDBox>}
        <MDBox margin="1rem 0 1rem 1rem">
          <Grid container spacing={3}>
            <Grid item xs={3}>
              {Boolean(customerOptions) && Boolean(customerOptions.length) && (
                <SelectFields
                  field={{
                    name: "",
                    label: "Customers",
                    value: customers,
                    errors: {},
                    multiple: true,
                  }}
                  options={["ALL CUSTOMER", ...customerOptions]}
                  onChange={(e, v) => {
                    if (v.includes("ALL CUSTOMER")) {
                      setCustomers(["ALL CUSTOMER"]);
                      setData([]);
                      setFilteredData([]);
                    } else {
                      setCustomers(v);
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item>
              <RadioGroup
                row
                onChange={(e) => {
                  setType(e.target.value);
                }}
              >
                <FormControlLabel
                  labelPlacement="left"
                  value="ALL"
                  label="All"
                  control={<Radio checked={type === "ALL"} />}
                />
                <FormControlLabel
                  labelPlacement="left"
                  value="EXPIRED"
                  label="Expired"
                  control={<Radio checked={type === "EXPIRED"} />}
                />
                <FormControlLabel
                  labelPlacement="left"
                  value="XEXPIRED"
                  label="Shelf Life Expiration"
                  control={<Radio checked={type === "XEXPIRED"} />}
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={4}>
              {isAllCustomer ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    onExportAllCustomer();
                  }}
                >
                  export
                </MDButton>
              ) : (
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    const filename = `${customerCode}_${dayjs(new Date()).format(
                      "YYYYMMDD-HHmm"
                    )}_ShelfLifeReport`;
                    downloadCsv(filename, filteredData);
                  }}
                >
                  export
                </MDButton>
              )}
            </Grid>
          </Grid>
        </MDBox>
        {!isAllCustomer && (
          <BasicTable
            data={data}
            columns={ShelfLifeReportEnquiryColumns}
            onFilterRowsChange={handleFilterRowsChange}
          />
        )}
      </TableCard>
    </ListLayout>
  );
}

export default ShelfLifeReportEnquiry;
